import { ComputedStaticTextInput } from '@framework/form/inputs/computed/ComputedStaticTextInput.tsx';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import styled from 'styled-components';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem.tsx';
import { Datatype } from '../../../../expressions/datatype/Datatype.ts';
import { type ExpressionValue } from '../../../../expressions/evaluate/ExpressionValue.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { formatComputedNumberValue } from '../../computed/number/computedNumberFormatter.ts';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { type ISwitchWidget } from './ISwitchWidget.ts';

const InputBorder = styled.div`
  border: 1px solid ${props => props.theme.colorBorder};
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 10px;
`;

function customRenderFormat(value: any, datatype: Datatype): string {
  // In caso di date, renderizziamo con il formato DD/MM/YYYY
  // per simulare un campo data.
  if (
    datatype === Datatype.Date &&
    typeof value === 'string' &&
    Boolean(value)
  ) {
    return dayjs(value, 'YYYY-MM-DD').format('DD/MM/YYYY').toString();
  }

  // Per i numeri, formattiamo con la virgola.
  if (datatype === Datatype.Number) {
    return formatComputedNumberValue(value);
  }

  return value?.toString() ?? '';
}

export interface IScoreWidgetComponentProps
  extends IWidgetComponentProps<ISwitchWidget> {}

export function SwitchWidgetComponent(props: IScoreWidgetComponentProps) {
  const { widget } = props;
  const options = getWidgetProps(props);

  const renderFormat = useCallback(
    (v: ExpressionValue) => customRenderFormat(v, widget.datatype),
    [widget.datatype],
  );

  return (
    <ConfigFormItem
      hidden={widget.layout?.hide}
      name={options.name}
      label={<RichTextAdvancedRender inline text={options.label} />}
      help={
        options?.helpText ? (
          <RichTextAdvancedRender inline text={options?.helpText} />
        ) : undefined
      }
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
      widget={props.widget}
    >
      <ComputedStaticTextInput
        name={options.name}
        removeBorder={widget.layout?.removeBorder}
        renderFormat={renderFormat}
      />
    </ConfigFormItem>
  );
}
